import Markdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';

/**
 * Renders a chat message body as safe markdown. `react-markdown` does not
 * render embedded raw HTML (no `rehype-raw` plugin) and sanitizes link URLs,
 * so customer-, AI- and operator-authored text is rendered without an XSS
 * surface. `remark-breaks` turns single newlines into <br> so chat line
 * breaks are preserved. Element styling uses design tokens only.
 */
const COMPONENTS: Components = {
  p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5">{children}</ol>,
  li: ({ children }) => <li className="my-0.5">{children}</li>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded-sm bg-accent px-1 py-0.5 font-mono text-meta">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-md bg-accent p-2 font-mono text-meta">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-muted-foreground">
      {children}
    </blockquote>
  ),
  h1: ({ children }) => <h1 className="text-base font-semibold">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold">{children}</h4>,
  h5: ({ children }) => <h5 className="text-base font-semibold">{children}</h5>,
  h6: ({ children }) => <h6 className="text-base font-semibold">{children}</h6>,
};

export function MessageMarkdown({ text }: Readonly<{ text: string }>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <Markdown remarkPlugins={[remarkBreaks]} components={COMPONENTS}>
        {text}
      </Markdown>
    </div>
  );
}
